/** @file Weights panel: edit-mesh click mode, id labels, keep-image and the topology actions. */
import type { CatalogKey } from '@pose2d/i18n/types';
import { useState } from 'react';
import { editMeshStore, type EditMeshMode, type EditMeshOptions } from '../../tools/editMesh/editMeshStore';
import { meshAction, type MeshActionId } from '../../tools/editMesh/meshAction';
import { selectVertices } from '../../tools/editMesh/selectVertices';
import { Button } from '../../ui-kit/Button';
import { Checkbox } from '../../ui-kit/Checkbox';
import type { SectionProps } from './BindingSection';

const MODES: readonly { mode: EditMeshMode; key: CatalogKey }[] = [
  { mode: 'modify', key: 'tools.editMesh.modify' },
  { mode: 'create', key: 'tools.editMesh.create' },
  { mode: 'delete', key: 'tools.editMesh.remove' },
];
const ACTIONS: readonly { id: MeshActionId; key: CatalogKey }[] = [
  { id: 'deleteVertices', key: 'tools.editMesh.deleteVertices' },
  { id: 'retriangulate', key: 'tools.editMesh.retriangulate' },
  { id: 'outlineHull', key: 'tools.editMesh.outlineHull' },
  { id: 'flipEdge', key: 'tools.editMesh.flipEdge' },
  { id: 'addTriangle', key: 'tools.editMesh.addTriangle' },
  { id: 'removeTriangle', key: 'tools.editMesh.removeTriangle' },
];

/** Topology is setup data: the actions are disabled in Animate mode, where the tool keys deforms instead. */
export function MeshSection({ view, doc, t, commit, options, animate }: SectionProps & { options: EditMeshOptions; animate: boolean }) {
  const [hint, setHint] = useState<CatalogKey | null>(null);
  const run = (id: MeshActionId): void => {
    const out = meshAction(id, doc, view.target, view.selected);
    setHint('hint' in out ? out.hint : null);
    if ('hint' in out) return;
    commit(out.calls);
    selectVertices(view.target.attachment, out.select, 'replace');
  };
  return (
    <section className="weights-section" aria-label={t('tools.editMesh.title')}>
      <div className="weights-buttons" role="group" aria-label={t('tools.editMesh.clickMode')}>
        {MODES.map((m) => <Button key={m.mode} label={t(m.key)} primary={options.mode === m.mode} onClick={() => { editMeshStore.setState({ mode: m.mode }); }} />)}
      </div>
      <Checkbox label={t('tools.editMesh.showIds')} checked={options.showIds} onChange={(showIds) => { editMeshStore.setState({ showIds }); }} />
      <Checkbox label={t('tools.editMesh.keepImage')} checked={options.keepImage} onChange={(keepImage) => { editMeshStore.setState({ keepImage }); }} />
      <div className="weights-buttons">
        {ACTIONS.map((a) => <Button key={a.id} label={t(a.key)} disabled={animate} onClick={() => { run(a.id); }} />)}
      </div>
      {animate ? <p className="weights-muted">{t('tools.editMesh.setupOnly')}</p> : null}
      {hint === null ? null : <p className="weights-muted" role="status">{t(hint)}</p>}
    </section>
  );
}
